import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  styled,
  useTheme,
  Link,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { getFirebaseApp, signIn, signUp, signOut, sendEmailVerification } from '../utils/firebase';
import ProfileSignin from './ProfileSignin';
import ProfileSignup from './ProfileSignup';
import ProfileResetPassword from './ProfileResetPassword';
import VerifyEmailComponent from './VerifyEmailComponent';
import { useAuth } from '../redux/slices/auth/useAuth';
import { useDispatch } from 'react-redux';
import { loginSetConfirmDetails, lyfshortLoginFailed } from '../redux/slices/auth/authSlice';
import { checkUserEmailVerification, updateUser } from '../redux/slices/user/userSlice';
import ProfileGetMinimumDetails from './ProfileGetMinimumDetails';
import { LyfshortUserProfile } from '../types/authState';
import OnboardingWelcome from './OnboardingWelcome';
import { ArrowBack } from '@mui/icons-material';

type LoginStep = 'login' | 'signup' | 'signup_onboarding' | 'reset' | 'discontinue-facebook' | 'onboarding'

const havingBottomLinks = [
  'login',
  'signup',
  'reset'
]

const havingArrowBack = [
  'login',
  'signup',
  'reset',
]

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch()
  const { isLoggedIn, progress } = useAuth()
  const theme = useTheme();

  useEffect(() => {
    if (isLoggedIn) {
      navigate('/expert/collect')
    }
  }, [isLoggedIn])


  const [loginStep, setLoginStep] = useState<LoginStep>('onboarding')
  const [inProgress, setInProgress] = useState(false);

  const onReturnUserPress = () => {
    setLoginStep('login')
  }

  const onNewUserPress = () => {
    setLoginStep('signup')
  }

  const onSignin = (email: string, password: string) => {
    setInProgress(true)
    signIn(email, password)
      .then(() => {
        setInProgress(false)
      })
      .catch((error) => {
        setInProgress(false)
        throw new Error(error)
      })
  }

  const onSignup = (email: string, password: string, name: string, extra: object) => {
    setInProgress(true)
    signUp(email, password, name, extra)
      .then(() => {
        setInProgress(false)
        setLoginStep('login')
      })
      .catch((error) => {
        setInProgress(false)
        throw new Error(error)
      })
  }

  const onResetPassword = (email: string) => {
    const firebaseAuth = getAuth(getFirebaseApp());
    sendPasswordResetEmail(firebaseAuth, email).then(() => setLoginStep('login'))
  }

  const onCancel = async () => {
    try {
      await signOut()
      const error = new Error('cancelled by user');
      dispatch(lyfshortLoginFailed(error))
    } catch (error) {
      console.error('Failed to log out:', error)
    }
  }

  const onResendVerificationEmail = () => {
    try {
      sendEmailVerification()
    } catch (error) {
      console.warn(error)
      // TODO: Handle error
    }
  }

  const onUpdateUser = (user: Partial<LyfshortUserProfile>) => {
    dispatch(loginSetConfirmDetails(false))
    dispatch(updateUser({
      user: user,
      requiresAccessToken: true
    }))
  }

  const onCheckEmailVerification = () => {
    dispatch(checkUserEmailVerification({
      requiresAccessToken: true
    }))
  }

  const onboarding = () => {
    return (
      <OnboardingWelcome
        onReturnUserPress={onReturnUserPress}
        onNewUserPress={onNewUserPress}
      />
    )
  }

  const signin = () => {
    return (
      <ProfileSignin onSingin={onSignin} inProgress={inProgress} />
    )
  }

  const signup = () => {
    return (
      <ProfileSignup onSingup={onSignup} inProgress={inProgress} />
    )
  }

  const reset = () => {
    return (
      <ProfileResetPassword onResetPassword={onResetPassword} />
    )
  }

  const verifyUserEmailScreen = () => {
    return (
      <VerifyEmailComponent
        onCancel={onCancel}
        onResendVerificationEmail={onResendVerificationEmail}
        onCheckEmailVerification={onCheckEmailVerification}
      />
    )
  }

  const profileGetMinimumDetailsScreen = () => {
    return (
      <ProfileGetMinimumDetails
        onCancel={onCancel}
        onUpdateUser={onUpdateUser}
      />
    )
  }

  const render = () => {
    if (progress === 'verify-email') {
      return verifyUserEmailScreen()
    } else if (progress === 'get-user-details') {
      return profileGetMinimumDetailsScreen()
    } else {
      switch (loginStep) {
        case 'onboarding':
          return onboarding();
        case 'login':
          return signin()
        case 'signup':
          return signup()
        case 'reset':
          return reset()
        default:
          return <CircularProgress />
      }
    }
  }

  return (
    <PageContainer>
      <LogoContainer>
        <Typography variant="h4" sx={{ color: 'white', fontWeight: 600 }}>
          Triplink.VIP
        </Typography>
      </LogoContainer>

      <Container maxWidth="sm">
        <StyledPaper elevation={3}>
          <Box px={4} py={6}>
            {!progress && havingArrowBack.includes(loginStep) &&<IconButton onClick={() => setLoginStep('onboarding')}>
              <ArrowBack />
            </IconButton>}
            {render()}
            {!progress &&
              <Box mt={2} display="flex" justifyContent="space-between">
                {havingBottomLinks.includes(loginStep) && (
                  <>
                    <Link
                      component="button"
                      onClick={() => {
                        if (loginStep === 'signup') {
                          setLoginStep('login')
                        } else if (loginStep === 'login') {
                          setLoginStep('signup')
                        }
                      }}
                      sx={{
                        color: theme.palette.primary.main,
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {loginStep === 'signup'
                        ? 'Already have an account? Sign in'
                        : "Don't have an account? Sign up"}
                    </Link>
                    <Link
                      component="button"
                      onClick={() => {
                        if (loginStep !== 'reset') {
                          setLoginStep('reset')
                        } else {
                          setLoginStep('login')
                        }
                      }}
                      sx={{
                        color: theme.palette.primary.main,
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {loginStep === 'reset'
                        ? 'Back to login'
                        : 'Forgot password?'}
                    </Link>
                  </>
                )}
              </Box>
            }
          </Box>
        </StyledPaper>
      </Container>
    </PageContainer>
  );
};

const PageContainer = styled(Box)`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #2C323C;
`;

const LogoContainer = styled(Box)`
  padding: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledPaper = styled(Paper)`
  border-radius: 8px;
  background-color: white;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
`;

export default LoginPage;
